import { useEffect, useRef } from 'react';
import { toast } from '@/components/ui/sonner';
import {
  currentEntryHash,
  extractEntryHash,
  shouldReloadForNewBuild,
} from '@/lib/buildVersion';

const RELOAD_FLAG = 'lysara.build-refresh.done';
const POLL_MS = 5 * 60_000;
const UPDATE_TOAST_ID = 'lysara-update-ready';
const SAFE_RELOAD_EVENT = 'lysara:request-safe-reload';

function hasOpenDialog(): boolean {
  return !!document.querySelector(
    '[role="dialog"][data-state="open"], [role="dialog"][aria-modal="true"], ' +
    '[role="alertdialog"][data-state="open"], [role="alertdialog"][aria-modal="true"]'
  );
}

function hasUnsavedFormChanges(): boolean {
  const controls = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
    'form input, form textarea, form select'
  );
  for (const control of controls) {
    if (control.disabled || control.type === 'hidden' || control.type === 'submit' ||
        control.type === 'button' || control.type === 'reset' || control.type === 'search') continue;
    if (control instanceof HTMLInputElement && (control.type === 'checkbox' || control.type === 'radio')) {
      if (control.checked !== control.defaultChecked) return true;
    } else if (control instanceof HTMLSelectElement) {
      if ([...control.options].some((option) => option.selected !== option.defaultSelected)) return true;
    } else if (control.value !== control.defaultValue) {
      return true;
    }
  }
  return false;
}

function hasPendingWork(): boolean {
  return hasOpenDialog() || hasUnsavedFormChanges();
}

/**
 * Picks up a new deploy on long-lived tablet sessions without a forced logout.
 * Polls the served index.html (no-store) and reloads once when the hashed
 * entry bundle changes. Auth/session storage is untouched.
 */
export function useBuildVersionRefresh(enabled = true) {
  const booted = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || import.meta.env.DEV) return;
    booted.current = currentEntryHash();
    if (!booted.current) return;

    let cancelled = false;
    let pendingBuild: string | null = null;
    let pendingReload = false;
    let updateNoticeShown = false;

    const applyWhenSafe = () => {
      if ((!pendingBuild && !pendingReload) || cancelled || document.visibilityState !== 'visible') return;
      if (hasPendingWork()) {
        if (!updateNoticeShown) {
          updateNoticeShown = true;
          toast.info('Update ready', {
            id: UPDATE_TOAST_ID,
            duration: Infinity,
            description: 'The update will apply after open dialogs are closed and form changes are saved or discarded.',
          });
        }
        return;
      }
      toast.dismiss(UPDATE_TOAST_ID);
      if (pendingBuild) sessionStorage.setItem(RELOAD_FLAG, pendingBuild);
      window.location.reload();
    };

    const check = async () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      if (pendingBuild) {
        applyWhenSafe();
        return;
      }
      try {
        const res = await fetch(`/index.html?v=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const latest = extractEntryHash(await res.text());
        const alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === latest;
        if (shouldReloadForNewBuild({
          current: booted.current,
          latest,
          alreadyReloaded,
        })) {
          pendingBuild = latest;
          applyWhenSafe();
        }
      } catch {
        // Offline / transient: the next poll retries.
      }
    };

    const onWorkStateChange = () => applyWhenSafe();
    const onFormReset = () => window.setTimeout(onWorkStateChange, 0);
    const onSafeReloadRequest = (event: Event) => {
      event.preventDefault();
      pendingReload = true;
      applyWhenSafe();
    };
    const observer = new MutationObserver(onWorkStateChange);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state'],
    });
    document.addEventListener('input', onWorkStateChange, true);
    document.addEventListener('change', onWorkStateChange, true);
    document.addEventListener('reset', onFormReset, true);
    window.addEventListener(SAFE_RELOAD_EVENT, onSafeReloadRequest);

    void check();
    const timer = window.setInterval(check, POLL_MS);
    document.addEventListener('visibilitychange', check);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      observer.disconnect();
      document.removeEventListener('input', onWorkStateChange, true);
      document.removeEventListener('change', onWorkStateChange, true);
      document.removeEventListener('reset', onFormReset, true);
      window.removeEventListener(SAFE_RELOAD_EVENT, onSafeReloadRequest);
      document.removeEventListener('visibilitychange', check);
      toast.dismiss(UPDATE_TOAST_ID);
    };
  }, [enabled]);
}
